import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Table, Button, Card, message, Popconfirm, Space, Tag, Radio, DatePicker, Tooltip,
  Modal, Input,
} from 'antd';
import { ReloadOutlined, SendOutlined, RollbackOutlined, EditOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useFinanceApi } from '../api/financeApi';
import { fmtAmount, fmtDate } from '../shared/format';
import { enhanceColumns, toplamGenislik } from '../../ortak/tableHelpers';

const TALEP_DURUM_COLOR = { Beklemede: 'gold', 'Onaylandı': 'blue', Reddedildi: 'red', 'Ödendi': 'green' };

/**
 * Satın Alma — onaylı hakedişleri ÖDEME TALEBİNE aktarma ekranı.
 * Ödeme yapılmaz: havuzdaki (onaylanmış, talebe bağlanmamış) hakedişler seçilir,
 * aynı cari+firma olanlar birleştirilerek ya da her biri ayrı ayrı ödeme talebine dönüştürülür.
 */
export default function SatinAlmaBulkTab({ perms }) {
  const api = useFinanceApi();
  const p = perms.satinalma || {};
  const canEdit = p.edit !== false;

  const [pool, setPool] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [birlestir, setBirlestir] = useState(true);
  const [vade, setVade] = useState(null);

  const loadAll = useCallback(async () => {
    if (p.view === false) return;
    setLoading(true);
    try {
      const [h, r] = await Promise.all([
        api.get('/bulk-payment/approved-hakedis'),
        api.get('/bulk-payment/requests'),
      ]);
      setPool(Array.isArray(h) ? h : []);
      setRequests(Array.isArray(r) ? r : []);
    } catch (e) {
      message.error(e?.response?.data?.detail || 'Veriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [p.view]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Seçime göre önizleme: kaç talep oluşacak, toplamlar ne olacak
  const preview = useMemo(() => {
    const rows = pool.filter((h) => selected.includes(h.id));
    const toplam = rows.reduce((s, h) => s + Number(h.odenecek_tutar || 0), 0);
    if (!birlestir) return { adet: rows.length, toplam, gruplar: rows.map((h) => [h]) };
    const g = {};
    rows.forEach((h) => {
      const k = `${h.cari_id}|${h.firma_id}`;
      (g[k] = g[k] || []).push(h);
    });
    return { adet: Object.keys(g).length, toplam, gruplar: Object.values(g) };
  }, [pool, selected, birlestir]);

  const doTransfer = async () => {
    try {
      const res = await api.post('/bulk-payment/create-requests', {
        hakedis_ids: selected,
        birlestir,
        vade: vade ? dayjs(vade).format('YYYY-MM-DD') : undefined,
      });
      const talepler = res?.talepler || [];
      message.success(`${selected.length} hakediş ${talepler.length || preview.adet} ödeme talebine aktarıldı`);
      setSelected([]);
      loadAll();
    } catch (e) {
      message.error(e?.response?.data?.detail || 'Aktarım hatası');
    }
  };

  const doUndo = async (r) => {
    try {
      await api.post('/bulk-payment/undo-request', { odeme_talebi_id: r.id });
      message.success('Aktarım geri alındı; hakedişler havuza döndü');
      loadAll();
    } catch (e) {
      message.error(e?.response?.data?.detail || 'Geri alma hatası');
    }
  };

  // Fatura kodu SONRADAN girilir (13.08): fatura geldiğinde Satın Alma buradan işler.
  const [faturaRow, setFaturaRow] = useState(null);
  const [faturaKod, setFaturaKod] = useState('');
  const [faturaSaving, setFaturaSaving] = useState(false);

  const openFatura = (r) => { setFaturaRow(r); setFaturaKod(r.fatura_kodu || ''); };

  const saveFatura = async () => {
    setFaturaSaving(true);
    try {
      const res = await api.post(`/accruals/${faturaRow.id}/fatura-kodu`, { fatura_kodu: faturaKod });
      if (res?.data?.mukerrer?.length) {
        message.warning(res.message, 7);
      } else {
        message.success(res?.message || 'Fatura kodu kaydedildi');
      }
      setFaturaRow(null);
      loadAll();
    } catch (e) {
      message.error(e?.response?.data?.detail || 'Kaydedilemedi');
    } finally {
      setFaturaSaving(false);
    }
  };

  if (p.view === false) return <div style={{ padding: 24 }}>Görüntüleme yetkiniz yok.</div>;

  const poolCols = [
    { title: '#', dataIndex: 'id', width: 70, render: (v) => `#${v}` },
    { title: 'Tarih', dataIndex: 'tarih', width: 100, render: fmtDate },
    { title: 'Cari', dataIndex: 'cari_ad', ellipsis: true },
    { title: 'Firma', dataIndex: 'firma_adi', ellipsis: true },
    { title: 'Matrah', dataIndex: 'matrah', width: 120, render: (v) => fmtAmount(v, 'TRY') },
    { title: 'KDV', dataIndex: 'kdv_tutar', width: 110, render: (v) => fmtAmount(v, 'TRY') },
    {
      title: 'Tevkifat', dataIndex: 'tevkifat_tutar', width: 120,
      render: (v, r) => (r.tevkifat_kodu ? `${r.tevkifat_kodu} · ${fmtAmount(v, 'TRY')}` : '-'),
    },
    { title: 'Ödenecek', dataIndex: 'odenecek_tutar', width: 130, render: (v) => <b>{fmtAmount(v, 'TRY')}</b> },
    { title: 'SAT No', dataIndex: 'sat_no', width: 100 },
    {
      title: 'Fatura Kodu', dataIndex: 'fatura_kodu', width: 150, noFilter: true,
      render: (v, r) => (
        <Space size={4}>
          {v ? <span>{v}</span> : <span style={{ color: '#bbb' }}>-</span>}
          {canEdit && (
            <Tooltip title={v ? 'Fatura kodunu düzenle' : 'Fatura kodu gir (fatura geldiğinde)'}>
              <Button size="small" type="text" icon={<EditOutlined />} onClick={() => openFatura(r)} />
            </Tooltip>
          )}
        </Space>
      ),
    },
    { title: 'Açıklama', dataIndex: 'aciklama', ellipsis: true },
  ];

  const reqCols = [
    { title: 'Talep', dataIndex: 'id', width: 80, render: (v) => `#${v}` },
    { title: 'Tarih', dataIndex: 'tarih', width: 100, render: fmtDate },
    { title: 'Vade', dataIndex: 'vade', width: 100, render: fmtDate },
    { title: 'Cari', dataIndex: 'cari_ad', ellipsis: true },
    { title: 'Firma', dataIndex: 'firma_adi', ellipsis: true },
    { title: 'Tutar', dataIndex: 'tutar', width: 130, render: (v) => <b>{fmtAmount(v, 'TRY')}</b> },
    {
      title: 'Hakedişler', dataIndex: 'hakedisler', ellipsis: true,
      render: (v, r) => `${r.hakedis_adet} adet: #${String(v || '').split(', ').join(', #')}`,
    },
    {
      title: 'Durum', dataIndex: 'durum', width: 110,
      render: (v) => <Tag color={TALEP_DURUM_COLOR[v] || 'default'}>{v}</Tag>,
    },
    ...(canEdit ? [{
      title: 'İşlem', key: 'a', fixed: 'right', width: 100, noFilter: true,
      render: (_, r) => r.durum === 'Beklemede' && (
        <Popconfirm title="Aktarım geri alınsın mı? (talep silinir, hakedişler havuza döner)"
          okText="Geri Al" cancelText="Vazgeç" onConfirm={() => doUndo(r)}>
          <Button size="small" danger icon={<RollbackOutlined />}>Geri Al</Button>
        </Popconfirm>
      ),
    }] : []),
  ];

  return (
    <div>
      <Space wrap style={{ marginBottom: 12 }}>
        <Button icon={<ReloadOutlined />} onClick={loadAll}>Yenile</Button>
        {canEdit && (
          <>
            <Radio.Group value={birlestir} onChange={(e) => setBirlestir(e.target.value)}
              optionType="button" buttonStyle="solid"
              options={[
                { value: true, label: 'Birleştir (aynı cari+firma)' },
                { value: false, label: 'Ayrı talepler' },
              ]} />
            <Tooltip title="Talep vadesi (boş = bugün)">
              <DatePicker placeholder="Vade" format="DD.MM.YYYY" value={vade} onChange={setVade} />
            </Tooltip>
            <Popconfirm
              title={`${selected.length} hakediş → ${preview.adet} ödeme talebi (${fmtAmount(preview.toplam, 'TRY')}). Onaylıyor musunuz?`}
              okText="Aktar" cancelText="Vazgeç" onConfirm={doTransfer} disabled={!selected.length}>
              <Button type="primary" icon={<SendOutlined />} disabled={!selected.length}>
                Ödeme Talebi Oluştur ({selected.length} hakediş → {preview.adet} talep)
              </Button>
            </Popconfirm>
            {selected.length > 0 && (
              <span style={{ color: '#888' }}>Seçili toplam: <b>{fmtAmount(preview.toplam, 'TRY')}</b></span>
            )}
          </>
        )}
      </Space>

      <Card size="small" title={`Aktarım Havuzu — Onaylı Hakedişler (${pool.length})`} style={{ marginBottom: 12 }}>
        <Table rowKey="id" size="small" loading={loading} dataSource={pool}
          columns={enhanceColumns(poolCols)} scroll={{ x: toplamGenislik(enhanceColumns(poolCols)), y: 360 }}
          pagination={{ pageSize: 20 }}
          rowSelection={canEdit ? {
            selectedRowKeys: selected,
            onChange: (keys) => setSelected(keys),
          } : undefined} />
      </Card>

      <Card size="small" title="Oluşturulan Ödeme Talepleri (hakediş kaynaklı)">
        <Table rowKey="id" size="small" loading={loading} dataSource={requests}
          columns={enhanceColumns(reqCols)} scroll={{ x: 'max-content' }}
          pagination={{ pageSize: 10 }} />
      </Card>

      <Modal
        title={`Fatura Kodu — Hakediş #${faturaRow?.id || ''}`}
        open={!!faturaRow}
        onOk={saveFatura}
        onCancel={() => setFaturaRow(null)}
        okText="Kaydet" cancelText="Vazgeç"
        confirmLoading={faturaSaving}
        destroyOnClose
      >
        <div style={{ color: '#888', fontSize: 12, marginBottom: 8 }}>
          Fatura geldiğinde kodu buradan girin. Aynı kod başka hakedişte varsa uyarı verilir,
          kayıt yine de yapılır.
        </div>
        <Input
          placeholder="Fatura kodu / numarası"
          value={faturaKod}
          onChange={(e) => setFaturaKod(e.target.value)}
          onPressEnter={saveFatura}
          maxLength={50}
        />
      </Modal>
    </div>
  );
}
